import { useEffect, useState, useRef, type ReactNode, useMemo } from "react";
import NameFilterBar from '../../components/NameFilterBar';
import { nachNamenFiltern } from '../../utilities/namensfilter';

import { userAPI } from "../../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { MarketplaceOffer, PromptTool, OfferStatus } from "@the-real-insight/tri-model";
import { useModalContext } from "../../components/ModalProvider";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useTestDialog } from "../hooks/useTestDialog";
import { useSearchParams } from "react-router-dom";
import truncate from "lodash.truncate";
import { useUrlPageState } from "../../utilities/dataTablePagination";
import OfferStatusManagement, { InternallyAvailableCheckbox } from '../OfferStatusManagement';
import { withMarketplaceMenuItems } from '../marketplacePublishing';
import MarketingDetails from '../MarketingDetails';
import SampleRequests from '../SampleRequests';
import PlanPanel from '../PlanPanel';
import ZodEditor from './ZodEditor';
import { FluentIconSelector } from "../../components/FluentIconSelector";
import { ColorSelector } from "../../components/ColorSelector";
import { estimateTokens, formatTokenEstimation } from "../../utilities/tokenEstimation";
import { ToolUsageList } from "../components/ToolUsageList";
import { MarketplaceOfferIcon, MarketplaceOfferNameCell } from "../components/MarketplaceOfferNameCell";
import { OfferRowMenu, YesNoBadge, rowMenuColumnMeta } from "../components/offerTable";
import { getKnowledgeBases } from "../../KnowledgeBasesAPI";

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Check,
    ChevronsUpDown,
    Bot,
    ExternalLink,
    Globe,
    Library,
    ListChecks,
    Loader2,
    Plus,
    Rocket,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Stepper } from '@/components/ui/stepper';
import { ChoiceCards, Disclosure, SideCard, StepLayout } from './wizardParts';
import { EASE_OUT } from '@/lib/ease';
import { AnimatePresence, motion } from 'motion/react';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { DataTable } from '@/components/ui/data-table';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import AgentStepPanel, { useToolCatalogue } from './AgentStepPanel';
import { STEP_SCHEMA_VERSION, withDefaults, validateAgentConfig,
    BLOCKING_TEXTE, GOAL_MAX_LENGTH,
} from './agentStepConfig';
import { fetchToolCatalogue } from './toolCatalogueApi';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

// ZodField interface matching the one from tri-model
interface ZodField {
    id: string;
    name: string;
    type: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'array' | 'object';
    required: boolean;
    description: string;
    defaultValue: string;
    children: ZodField[];
    arrayType?: 'string' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'file' | 'object';
}

// ZodField type is now imported from @the-real-insight/tri-model

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                : sorted === 'desc'
                    ? <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />}
        </Button>
    );
}

/** Section keys that exist only in the assistant that creates a tool, not in the editor's side menu. */
const CREATE_ONLY_KEYS = ['mode', 'settings', 'agent', 'appearance'];

interface ComboBoxOption {
    key: any;
    text: string;
}

/**
 * Ersetzt die abgelöste ComboBox: Auswahlfeld mit Tippsuche (Popover + cmdk).
 * Meldet wie zuvor die gewählte Option zurück, ohne selbst Zustand zu halten.
 */
function ComboBoxField({
    id,
    label,
    options,
    selectedKey,
    onSelect,
    placeholder,
    disabled,
    emptyText,
}: {
    id: string;
    label: string;
    options: ComboBoxOption[];
    selectedKey?: any;
    onSelect: (key: any) => void;
    placeholder: string;
    disabled?: boolean;
    emptyText: string;
}) {
    const [open, setOpen] = useState(false);
    const selected = options.find(option => option.key === selectedKey);

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={id}>{label}</Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className="w-full justify-between font-normal"
                    >
                        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
                            {selected ? selected.text : placeholder}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                        <CommandInput placeholder={placeholder} />
                        <CommandList>
                            <CommandEmpty>{emptyText}</CommandEmpty>
                            <CommandGroup>
                                {options.map(option => (
                                    <CommandItem
                                        key={String(option.key)}
                                        value={option.text}
                                        onSelect={() => {
                                            onSelect(option.key);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4',
                                                option.key === selectedKey ? 'opacity-100' : 'opacity-0',
                                            )}
                                            aria-hidden
                                        />
                                        <span className="truncate">{option.text}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}

export default function PromptToolManagement() {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, pushMessage, showDeleteDialog } = useModalContext() as any;
    const { TestDialog, openTestDialog } = useTestDialog();
    const [searchParams, setSearchParams] = useSearchParams();
    const { page, onPageChange } = useUrlPageState('promptToolPage');
    const [promptTools, setPromptTools] = useState<any[]>([]);
    /* Suchfeld über der Tabelle: Teilzeichenkette im Namen, siehe utilities/namensfilter.ts. */
    const [promptToolFilter, setPromptToolFilter] = useState('');
    const gefiltertePromptTools = useMemo(
        () => nachNamenFiltern(promptTools, promptToolFilter),
        [promptTools, promptToolFilter]
    );
    const [selectedPromptTool, setSelectedPromptTool] = useState<any>();
    // Starts true: the first frame shows the table skeleton, not an empty-state flash.
    const [loadingPromptTools, setLoadingPromptTools] = useState(true);
    const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
    const [loadingKnowledgeBases, setLoadingKnowledgeBases] = useState(false);
    // Auf Komponentenebene, nicht im Zweig des Schritt-Typs: Hooks dürfen nicht bedingt laufen.
    const { tools: toolCatalogue, error: toolCatalogueError } = useToolCatalogue(
        fetchToolCatalogue,
        i18next.language ?? "de"
    );
    const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
    const [linkUrl, setLinkUrl] = useState<string>('');
    const initialKey = searchParams.get('key') || 'prompt'; // Default to Prompt tab
    const [selectedKey, setSelectedKey] = useState<any>(initialKey);
    // Ersetzt den internen Zustand des abgelösten Pivot: beide Reiter bleiben
    // gemountet (die Schema-Editoren werden über Refs ausgelesen).
    const [inputOutputTab, setInputOutputTab] = useState<string>('input');
    const inputZodEditorRef = useRef<any>(null);
    const outputZodEditorRef = useRef<any>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    /**
     * Das Feld, das nach dem Wechsel in *Eingabe- und Ausgabedaten* zum Bearbeiten aufgehen soll.
     *
     * Der Umweg über den Zustand ist nötig, weil die Schema-Editoren erst in jenem Abschnitt
     * gemountet werden: Beim Klick auf ein Kennzeichen gibt es sie noch gar nicht, ihre Refs sind
     * leer. Erst nach dem Rendern des Abschnitts lässt sich der Dialog öffnen — das erledigt der
     * Effekt weiter unten.
     */
    const [zuOeffnendesFeld, setZuOeffnendesFeld] = useState<{
        bereich: 'input' | 'output';
        kennzeichen: string;
    } | null>(null);

    /**
     * **Ein Kennzeichen führt zu seinem Feld.**
     *
     * Bis hierher waren die Kennzeichen über und unter dem Prompt reine Anzeige: Sie nennen die
     * Ein- und Ausgabefelder, und wer eines ändern wollte, musste den Abschnitt wechseln, den
     * richtigen Reiter wählen und das Feld in der Liste suchen. Der Klick nimmt diesen Weg ab.
     */
    const parameterOeffnen = (bereich: 'input' | 'output', parameter: ZodField) => {
        setSelectedKey('inputOutput');
        updateURLFromSelectedKey('inputOutput');
        setInputOutputTab(bereich);
        setZuOeffnendesFeld({ bereich, kennzeichen: parameter.id || parameter.name });
    };

    // Helper function to render parameter chips
    const renderParameterChips = (
        parameters: ZodField[],
        defaultLabel: string,
        defaultDescription: string,
        bereich?: 'input' | 'output'
    ) => {
        if (!parameters || parameters.length === 0) {
            return (
                <div className="flex flex-wrap justify-center gap-2">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help rounded-full bg-[color:var(--customer-secondary,hsl(var(--primary)))] px-2 py-1 text-xs font-medium text-white">
                                    {defaultLabel}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{defaultDescription}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            );
        }

        return (
            <div className="flex flex-wrap justify-center gap-2">
                {parameters.map((param) => (
                    <TooltipProvider key={param.id}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    // Ohne Bereich gibt es nichts zu öffnen — dann bleibt es Anzeige.
                                    disabled={!bereich}
                                    onClick={() => bereich && parameterOeffnen(bereich, param)}
                                    className="rounded-full bg-[color:var(--customer-secondary,hsl(var(--primary)))] px-2 py-1 text-xs font-medium text-white outline-none ring-offset-background transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-help disabled:opacity-100"
                                >
                                    {param.name || param.id}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {param.description || t('ui.noDescriptionAvailable')}
                                {bereich && (
                                    <div className="mt-1 opacity-80">{t('ui.clickToEditField')}</div>
                                )}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ))}
            </div>
        );
    };

    // Helper function to render gray triangle. Beide Varianten zeigen nach unten:
    // die 'up'-Variante war im Bestand zusätzlich um 180° gedreht.
    const renderTriangle = (direction: 'down' | 'up') => (
        <div
            aria-hidden
            className={cn(
                'mx-auto h-0 w-0 border-x-8 border-x-transparent border-t-8 border-t-muted-foreground',
                direction === 'down' ? 'mb-2' : 'mt-2',
            )}
        ></div>
    );

    const deletePromptTool = (promptTool: any) => {
        try {
            showDeleteDialog(`${t('ui.deletePromptTool')} ${promptTool.name}`,
                promptTool.name,
                async () => {
                    await userAPI.deleteMarketplaceOffer(authentication.customer, promptTool);
                    setPromptTools(promptTools.filter((pt: any) => pt._id !== promptTool._id));
                });
        } catch (error) {
            pushError(error);
        }
    };

    const promptToolColumns: ColumnDef<any>[] = [{
        id: 'name',
        size: 340,
        accessorFn: (promptTool: any) => promptTool.name,
        header: ({ column }) => <SortableHeader column={column}>{t('ui.name')}</SortableHeader>,
        cell: ({ row }) => <MarketplaceOfferNameCell offer={row.original} />,
    }, {
        id: 'published',
        size: 160,
        accessorFn: (promptTool: any) => promptTool.published,
        header: ({ column }) => <SortableHeader column={column}>{t('ui.internallyAvailable')}</SortableHeader>,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.published)} />,
    }, {
        id: 'offered',
        size: 180,
        accessorFn: (promptTool: any) => promptTool.offered,
        header: ({ column }) => <SortableHeader column={column}>{t('ui.offeredInMarketplace')}</SortableHeader>,
        cell: ({ row }) => <YesNoBadge active={Boolean(row.original.offered)} />,
    }, {
        id: 'description',
        size: 400,
        header: () => <span className="font-bold text-foreground">{t('ui.description')}</span>,
        cell: ({ row }) => (
            <div className="line-clamp-2 text-muted-foreground">
                {truncate(row.original.synopsis, { length: 200, separator: /,? +/ })}
            </div>
        ),
    }, {
        id: 'rowMenu',
        size: 56,
        header: () => <span className="sr-only">{t('ui.actions', 'Aktionen')}</span>,
        meta: rowMenuColumnMeta,
        cell: ({ row }) => (
            <OfferRowMenu
                onEdit={() => updateSelectedPromptTool(row.original)}
                onTest={() => openTestDialog(row.original)}
                onDelete={() => deletePromptTool(row.original)}
            />
        ),
    }];

    const loadPromptTools = async () => {
        try {
            setLoadingPromptTools(true);
            // Try to get marketplace offers for the customer
            const marketplaceOffers = await userAPI.getMarketplaceOffers(authentication.customer);

            // Filter for PromptTool entities
            const promptTools = marketplaceOffers.filter((offer: any) => offer._type === 'PromptTool');

            setPromptTools(promptTools);
        } catch (error) {
            pushError(error);
        } finally {
            setLoadingPromptTools(false);
        }
    };

    const loadKnowledgeBases = async () => {
        try {
            setLoadingKnowledgeBases(true);
            const bases = await getKnowledgeBases(authentication.customer);
            setKnowledgeBases(bases);
        } catch (err) {
            console.error('Failed to load knowledge bases:', err);
        } finally {
            setLoadingKnowledgeBases(false);
        }
    };

    const updateSelectedPromptTool = (promptTool: any) => {
        if (promptTool) {
            const newPromptTool = new PromptTool(promptTool);
            Object.assign(newPromptTool, promptTool);
            setSelectedPromptTool(newPromptTool);
        } else {
            setSelectedPromptTool(null);
        }

        if (promptTool && promptTool._id) {
            searchParams.set('promptToolId', promptTool._id);
            setSearchParams(searchParams, { replace: true });
        } else {
        // When closing (promptTool is null), clean up all tool-related parameters
            searchParams.delete('promptToolId');
            searchParams.delete('key'); // Remove key parameter to go back to main view
            setSearchParams(searchParams, { replace: true });
        }

        // Reset unsaved changes when selecting a new tool
        setHasUnsavedChanges(false);
    };

    // Wrapper function for updates that should mark as unsaved
    const updateSelectedPromptToolWithChanges = (promptTool: any) => {
        if (promptTool) {
            const newPromptTool = new PromptTool(promptTool);
            Object.assign(newPromptTool, promptTool);
            setSelectedPromptTool(newPromptTool);
            setHasUnsavedChanges(true);
        }
    };

    const createNewPromptTool = () => {
        const newPromptTool = new PromptTool({
            name: '',
            description: '',
            synopsis: '',
            prompt: '',
            sampleRequests: [],
            links: [],
            published: false,
            _type: 'PromptTool',
            // A new tool starts as an agent (it covers "only the AI" as well); old tools keep their mode.
            promptMode: 'agent',
            agent: withDefaults(undefined),
            stepSchemaVersion: STEP_SCHEMA_VERSION,
            maxResults: 10,
            includeImages: false,
            includeVideos: false,
            includeAnswers: false,
            includeAnswer: 'basic',
            searchDepth: 3
        });

        setSelectedPromptTool(newPromptTool);
        setSelectedKey('general');
        searchParams.set('key', 'general');
        setSearchParams(searchParams, { replace: true });
        setHasUnsavedChanges(false);
    };

    /**
     * Sagt, WAS das Speichern verhindert — nicht, was es alles verhindern könnte.
     *
     * Gibt zurück, ob gespeichert werden darf; die drei Speicherwege fragen dasselbe und sollen
     * nicht dreimal denselben Satz zusammensetzen.
     */
    const darfGespeichertWerden = (promptTool: any): boolean => {
        if (promptTool?.promptMode !== 'agent') return true;
        const validation = validateAgentConfig(withDefaults(promptTool.agent));
        if (!validation.blocking.length) return true;
        const gruende = validation.blocking.map(grund =>
            t(BLOCKING_TEXTE[grund] ?? grund, { max: GOAL_MAX_LENGTH })
        );
        /*
         * `pushMessage`, nicht `pushError`: Eine blockierte Speicherung ist keine Ausnahme,
         * sondern eine Regel — und `pushError` zeigt bei einem einfachen `Error` nur „Es ist ein
         * Fehler aufgetreten" (siehe ModalProvider: den Text übernimmt es allein von einem
         * `ApplicationError`). Gemessen am 18.09.2026: Der Grund stand vollständig in der
         * Browser-Konsole und nirgends auf dem Schirm.
         */
        pushMessage([t('ui.agentStep.validationBlocksSave'), ...gruende].join(' '));
        return false;
    };

    // Save function for the prompt tool
    const savePromptTool = async () => {
        if (!selectedPromptTool) return;

        if (!darfGespeichertWerden(selectedPromptTool)) return;

        // Get current schema from ZodEditor refs
        let updatedPromptTool = new PromptTool(selectedPromptTool);
        Object.assign(updatedPromptTool, selectedPromptTool);

        if (inputZodEditorRef.current) {
            const inputSchema = inputZodEditorRef.current.getCurrentSchema();
            updatedPromptTool.setInputSchemaFields(inputSchema.fields);
        }

        if (outputZodEditorRef.current) {
            const outputSchema = outputZodEditorRef.current.getCurrentSchema();
            updatedPromptTool.setOutputSchemaFields(outputSchema.fields);
        }

        await userAPI.updateMarketplaceOffer(authentication.customer, updatedPromptTool);
        await loadPromptTools();
        setHasUnsavedChanges(false);

        // Update the current selected tool with the saved version
        const savedTools = await userAPI.getMarketplaceOffers(authentication.customer);
        const updatedTool = savedTools.find((t: any) => t._id === updatedPromptTool._id);
        if (updatedTool) {
            const refreshedTool = new PromptTool(updatedTool);
            Object.assign(refreshedTool, updatedTool);
            setSelectedPromptTool(refreshedTool);
        }
    };

    const menuItems = withMarketplaceMenuItems([
        {
            key: 'general',
            icon: 'Global',
            name: t('ui.generalInformation'),
            title: t('ui.add'),
            onClick: () => {
                setSelectedKey('general');
                updateURLFromSelectedKey('general');
            },
        },
        {
            key: 'offerStatus',
            icon: 'StatusCircleCheckmark',
            name: t('ui.offerStatus'),
            title: t('ui.offerStatus'),
            onClick: () => {
                setSelectedKey('offerStatus');
                updateURLFromSelectedKey('offerStatus');
            },
        },
        {
            key: 'prompt',
            icon: 'Code',
            name: t('ui.prompt'),
            title: t('ui.prompt'),
            onClick: () => {
                setSelectedKey('prompt');
                updateURLFromSelectedKey('prompt');
            },
        },
        {
            key: 'inputOutput',
            icon: 'DataConnectionLibrary',
            name: t('ui.inputOutputData'),
            title: t('ui.inputOutputData'),
            onClick: () => {
                setSelectedKey('inputOutput');
                updateURLFromSelectedKey('inputOutput');
            },
        },
        {
            key: 'sampleRequests',
            icon: 'SampleRequests',
            name: t('ui.sampleRequests'),
            title: t('ui.sampleRequests'),
            onClick: () => {
                setSelectedKey('sampleRequests');
                updateURLFromSelectedKey('sampleRequests');
            },
        },
        {
            key: 'details',
            icon: 'Details',
            name: t('ui.offerDetails'),
            title: t('ui.offerDetails'),
            onClick: () => {
                setSelectedKey('details');
                updateURLFromSelectedKey('details');
            },
        },
        {
            key: 'billing',
            icon: 'Billing',
            name: t('ui.billing'),
            title: t('ui.billing'),
            onClick: () => {
                setSelectedKey('billing');
                updateURLFromSelectedKey('billing');
            },
        }
    ], authentication?.customerObject);

    // Get valid menu keys for this tool type
    const validMenuKeys = menuItems.map(item => item.key);

    // Validate and fix selectedKey if it's not valid for this tool type
    const _validateAndFixSelectedKey = () => {
        if (!validMenuKeys.includes(selectedKey)) {
            setSelectedKey('prompt'); // Fallback to Prompt tab

            // Update URL to reflect the fallback
            const newParams = new URLSearchParams(searchParams);
            newParams.set('key', 'prompt');
            setSearchParams(newParams, { replace: true });
            return true; // Indicates we made a change
        }
        return false; // No change needed
    };

    const onImagesChange = async (imageList: any, _addUpdateIndex: any) => {
        try {
            const form = new FormData();
            form.append("file", imageList[0].file);
            const image = await userAPI.uploadFile(form);
            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, images: [...(selectedPromptTool.images || []), image.path] });
        } catch (error: any) {
            pushError(error);
        }
    };






    useEffect(() => {
        loadPromptTools();

    }, [authentication.customer]);

    useEffect(() => {
        if (authentication?.customer) {
            loadKnowledgeBases();
        }
    }, [authentication]);

    useEffect(() => {
        // Load prompt tool from URL if promptToolId is present
        const promptToolId = searchParams.get('promptToolId');
        if (promptToolId && promptToolId !== 'new' && promptTools.length > 0) {
            const promptTool = promptTools.find(pt => pt._id === promptToolId);
            if (promptTool && !selectedPromptTool) {
                updateSelectedPromptTool(promptTool);

            }
        }
    }, [searchParams, promptTools]);

    useEffect(() => {
        const keyParam = searchParams.get('key') || 'prompt'; // Default to Prompt tab

        if (keyParam !== selectedKey) {
            // Check if the key is valid for prompt tools before setting it
            const creatingTool = Boolean(selectedPromptTool) && !selectedPromptTool._id;
            if (validMenuKeys.includes(keyParam) || (creatingTool && CREATE_ONLY_KEYS.includes(keyParam))) {
                setSelectedKey(keyParam);
            } else {
                setSelectedKey('prompt'); // Fallback to Prompt tab

                // Update URL to reflect the fallback
                const newParams = new URLSearchParams(searchParams);
                newParams.set('key', 'prompt');
                setSearchParams(newParams, { replace: true });
            }
        }
    }, [searchParams, validMenuKeys]);

    // Helper function to update URL when user clicks menu items
    /*
     * **Erst wenn der Abschnitt steht, lässt sich der Dialog öffnen.**
     *
     * Die beiden Schema-Editoren werden nur im Abschnitt *Eingabe- und Ausgabedaten* gemountet;
     * zum Zeitpunkt des Klicks auf ein Kennzeichen sind ihre Refs leer. Dieser Effekt läuft nach
     * dem Rendern und holt das Öffnen nach. Findet er das Feld nicht (umbenannt, gelöscht, noch
     * nicht gespeichert), bleibt der Nutzer im Abschnitt stehen — das ist immer noch dort, wo er
     * hinwollte.
     */
    useEffect(() => {
        if (!zuOeffnendesFeld || selectedKey !== 'inputOutput') return;
        if (inputOutputTab !== zuOeffnendesFeld.bereich) return;
        // The editors may only mount after this render (the assistant opens their fold first): ask again for a moment.
        let attempts = 0;
        let handle: number | undefined;
        const attempt = () => {
            const editor =
                zuOeffnendesFeld.bereich === 'input' ? inputZodEditorRef.current : outputZodEditorRef.current;
            if (editor || ++attempts >= 30) {
                editor?.feldBearbeiten?.(zuOeffnendesFeld.kennzeichen);
                setZuOeffnendesFeld(null);
                return;
            }
            handle = window.setTimeout(attempt, 50);
        };
        attempt();
        return () => window.clearTimeout(handle);
    }, [zuOeffnendesFeld, selectedKey, inputOutputTab]);

    const updateURLFromSelectedKey = (newKey: string) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('key', newKey);
        setSearchParams(newParams, { replace: true });
    };

    /**
     * One section of the editor (general data, prompt, input/output, …). The editor of an existing
     * tool shows the one picked in its side menu; the stepper that creates a tool shows the one of
     * its current step — the same content in both.
     */
    const renderSection = (sectionKey: string): ReactNode => {
        switch (sectionKey) {
                                case 'general':
                                    return (
                                        <div className="w-full min-w-0">
                                            <div className="flex flex-col gap-5 md:flex-row">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-col gap-1.5">
                                                        <Label htmlFor="prompt-tool-name">{t('ui.promptToolName')}</Label>
                                                        <Input
                                                            id="prompt-tool-name"
                                                            value={selectedPromptTool.name}
                                                            onChange={(event) => {
                                                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, name: event.target.value });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3 flex flex-col gap-1.5">
                                                        <Label htmlFor="prompt-tool-description">{t('ui.promptToolDescription')}</Label>
                                                        <Textarea
                                                            id="prompt-tool-description"
                                                            value={selectedPromptTool.description}
                                                            rows={15}
                                                            onChange={(event) => {
                                                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, description: event.target.value });
                                                            }}
                                                        />
                                                    </div>
                                                    <p className="mt-3 text-sm text-muted-foreground">{t('ui.shortDescriptionUsedByAIForPromptTool')}</p>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap gap-5">
                                                        <FluentIconSelector
                                                            label="Icon"
                                                            selectedIcon={selectedPromptTool.icon || 'CityNext'}
                                                            iconColor={selectedPromptTool.iconColor}
                                                            onIconChange={(iconName) => {
                                                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, icon: iconName });
                                                            }}
                                                            placeholder={t('ui.selectIcon')}
                                                        />
                                                        <ColorSelector
                                                            label="Icon Color"
                                                            selectedColor={selectedPromptTool.iconColor}
                                                            onColorChange={(color) => {
                                                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, iconColor: color });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <InternallyAvailableCheckbox
                                                            marketplaceOffer={selectedPromptTool}
                                                            updateSelectedMarketplaceOffer={updateSelectedPromptToolWithChanges}
                                                        />
                                                    </div>

                                                    {selectedPromptTool._id && (
                                                        <div className="mt-3">
                                                            <ToolUsageList
                                                                toolId={selectedPromptTool._id}
                                                                hasUnsavedChanges={hasUnsavedChanges}
                                                                onSave={savePromptTool}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                case 'offerStatus':
                                    return (
                                        <OfferStatusManagement
                                            marketplaceOffer={selectedPromptTool}
                                            updateSelectedMarketplaceOffer={updateSelectedPromptToolWithChanges}
                                        />
                                    );
                                case 'prompt':
                                    const tokenCount = estimateTokens(selectedPromptTool.prompt || '');
                                    const tokenEstimation = formatTokenEstimation(tokenCount);

                                    const promptModeOptions = [
                                        { key: 'llmPrompt', text: t('ui.llmPrompt') },
                                        { key: 'webResearch', text: t('ui.webResearch') },
                                        { key: 'knowledgeBase', text: t('ui.knowledgeBasePrompt') },
                                        { key: 'agent', text: t('ui.agentStep.mode') },
                                        { key: 'planning', text: t('ui.planning') }
                                    ];

                                    const answerTypeOptions: ComboBoxOption[] = [
                                        { key: 'basic', text: t('ui.basic') },
                                        { key: 'advanced', text: t('ui.advanced') }
                                    ];
                                    const searchModeOptions: ComboBoxOption[] = [
                                        { key: 'simple', text: t('ui.simpleWebSearch') },
                                        { key: 'crawling', text: t('ui.internetCrawling') }
                                    ];

                                    return (
                                        <div className="w-full min-w-0 max-w-[1000px]">
                                            <div className="mb-4 flex flex-col gap-2">
                                                <Label id="prompt-mode-label">{t('ui.promptMode')}</Label>
                                                <RadioGroup
                                                    aria-labelledby="prompt-mode-label"
                                                    className="flex flex-row flex-wrap gap-5"
                                                    value={selectedPromptTool.promptMode || 'llmPrompt'}
                                                    onValueChange={(value) => {
                                                        // `agent` wird NICHT mitgelöscht: Wer versehentlich umstellt und
                                                        // zurückgeht, soll Werkzeugauswahl und Budget wiederfinden.
                                                        updateSelectedPromptToolWithChanges({
                                                            ...selectedPromptTool,
                                                            promptMode: value,
                                                            ...(value === 'agent'
                                                                ? {
                                                                      agent: withDefaults(selectedPromptTool.agent),
                                                                      stepSchemaVersion: STEP_SCHEMA_VERSION,
                                                                  }
                                                                : {}),
                                                        });
                                                    }}
                                                >
                                                    {promptModeOptions.map((option) => (
                                                        <div key={option.key} className="flex items-center gap-2">
                                                            <RadioGroupItem value={option.key} id={`prompt-mode-${option.key}`} />
                                                            <Label htmlFor={`prompt-mode-${option.key}`} className="cursor-pointer font-normal">
                                                                {option.text}
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </RadioGroup>
                                            </div>

                                            <div className="flex flex-col gap-6 md:flex-row">
                                                <div className="min-w-0 flex-1">
                                                    {selectedPromptTool.promptMode === 'agent' ? (
                                                    <AgentStepPanel
                                                        config={withDefaults(selectedPromptTool.agent)}
                                                        tools={toolCatalogue}
                                                        catalogueError={toolCatalogueError}
                                                        onChange={(agent) =>
                                                            updateSelectedPromptToolWithChanges({
                                                                ...selectedPromptTool,
                                                                agent,
                                                                stepSchemaVersion: STEP_SCHEMA_VERSION,
                                                            })
                                                        }
                                                    />
                                                    ) : (
                                                    <div>
                                                        <div className="mb-2 text-sm font-bold">{t('ui.prompt')}</div>

                                                        {/* Input Parameters Chips with gap */}
                                                        <div className="mb-4">
                                                            {renderParameterChips(
                                                                selectedPromptTool.getInputSchemaFields ? selectedPromptTool.getInputSchemaFields() : [],
                                                                t('ui.userInput'),
                                                                t('ui.userInputDescription'),
                                                                'input'
                                                            )}
                                                        </div>

                                                        {/* Triangle pointing down from input chips to text input */}
                                                        {renderTriangle('down')}

                                                        <Textarea
                                                            aria-label={t('ui.prompt')}
                                                            value={selectedPromptTool.prompt}
                                                            rows={20}
                                                            onChange={(event) => {
                                                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, prompt: event.target.value });
                                                            }}
                                                            placeholder={t('ui.enterPromptHere')}
                                                            className="bg-background font-mono text-xs"
                                                        />

                                                        {/* Triangle pointing up from text input to output chips */}
                                                        {renderTriangle('up')}

                                                        {/* Output Parameters Chips with gap */}
                                                        <div className="mt-4">
                                                            {renderParameterChips(
                                                                selectedPromptTool.getOutputSchemaFields ? selectedPromptTool.getOutputSchemaFields() : [],
                                                                t('ui.promptOutput'),
                                                                t('ui.promptOutputDescription'),
                                                                'output'
                                                            )}
                                                        </div>

                                                        <div className="mt-2 text-right text-xs text-muted-foreground">
                                                            {tokenEstimation}
                                                        </div>
                                                    </div>
                                                    )}
                                                </div>

                                                {selectedPromptTool.promptMode === 'webResearch' ? (
                                                    <div className="min-w-0 md:w-[300px] md:shrink-0">
                                                        <div className="mb-4 flex flex-wrap items-end gap-5">
                                                            <div className="min-w-0 flex-1">
                                                                <ComboBoxField
                                                                    id="prompt-tool-search-mode"
                                                                    label={t('ui.searchMode')}
                                                                    options={searchModeOptions}
                                                                    selectedKey={selectedPromptTool.searchMode || 'simple'}
                                                                    placeholder={t('ui.pleaseSelect')}
                                                                    emptyText={t('ui.noOptions')}
                                                                    onSelect={(key) => {
                                                                        updateSelectedPromptToolWithChanges({ ...selectedPromptTool, searchMode: key });
                                                                    }}
                                                                />
                                                            </div>
                                                            {selectedPromptTool.searchMode === 'crawling' && (
                                                                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                                                    <Label htmlFor="prompt-tool-search-depth">{t('ui.searchDepth')}</Label>
                                                                    <Input
                                                                        id="prompt-tool-search-depth"
                                                                        type="number"
                                                                        value={(selectedPromptTool.searchDepth ?? 3).toString()}
                                                                        onChange={(event) => {
                                                                            const v = parseInt(event.target.value || '0');
                                                                            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, searchDepth: isNaN(v) ? undefined : v });
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="mb-4 flex flex-col gap-1.5">
                                                            <Label htmlFor="prompt-tool-max-results">{t('ui.maxResults')}</Label>
                                                            <Input
                                                                id="prompt-tool-max-results"
                                                                type="number"
                                                                value={selectedPromptTool.maxResults?.toString() || '10'}
                                                                onChange={(event) => {
                                                                    updateSelectedPromptToolWithChanges({ ...selectedPromptTool, maxResults: parseInt(event.target.value || '10') });
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="mb-4">
                                                            <ComboBoxField
                                                                id="prompt-tool-include-answer"
                                                                label={t('ui.includeAnswer')}
                                                                options={answerTypeOptions}
                                                                selectedKey={selectedPromptTool.includeAnswer || 'basic'}
                                                                placeholder={t('ui.pleaseSelect')}
                                                                emptyText={t('ui.noOptions')}
                                                                onSelect={(key) => {
                                                                    updateSelectedPromptToolWithChanges({ ...selectedPromptTool, includeAnswer: key });
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="mb-2 flex items-center gap-2">
                                                            <Checkbox
                                                                id="prompt-tool-include-images"
                                                                checked={selectedPromptTool.includeImages || false}
                                                                onCheckedChange={(checked) => {
                                                                    updateSelectedPromptToolWithChanges({ ...selectedPromptTool, includeImages: checked === true });
                                                                }}
                                                            />
                                                            <Label htmlFor="prompt-tool-include-images" className="cursor-pointer font-normal">
                                                                {t('ui.includeImages')}
                                                            </Label>
                                                        </div>
                                                        <div className="mb-2 flex items-center gap-2">
                                                            <Checkbox
                                                                id="prompt-tool-include-videos"
                                                                checked={selectedPromptTool.includeVideos || false}
                                                                onCheckedChange={(checked) => {
                                                                    updateSelectedPromptToolWithChanges({ ...selectedPromptTool, includeVideos: checked === true });
                                                                }}
                                                            />
                                                            <Label htmlFor="prompt-tool-include-videos" className="cursor-pointer font-normal">
                                                                {t('ui.includeVideos')}
                                                            </Label>
                                                        </div>
                                                        <div className="mb-2 flex items-center gap-2">
                                                            <Checkbox
                                                                id="prompt-tool-include-answers"
                                                                checked={selectedPromptTool.includeAnswers || false}
                                                                onCheckedChange={(checked) => {
                                                                    updateSelectedPromptToolWithChanges({ ...selectedPromptTool, includeAnswers: checked === true });
                                                                }}
                                                            />
                                                            <Label htmlFor="prompt-tool-include-answers" className="cursor-pointer font-normal">
                                                                {t('ui.includeAnswers')}
                                                            </Label>
                                                        </div>
                                                    </div>
                                                ) : selectedPromptTool.promptMode === 'knowledgeBase' ? (
                                                    <div className="min-w-0 md:w-[300px] md:shrink-0">
                                                        <ComboBoxField
                                                            id="prompt-tool-knowledge-base"
                                                            label={t('ui.selectKnowledgeBase')}
                                                            options={knowledgeBases.map(kb => ({
                                                                key: kb._id,
                                                                text: kb.name
                                                            }))}
                                                            selectedKey={selectedPromptTool.knowledgeBase}
                                                            onSelect={(key) => {
                                                                updateSelectedPromptToolWithChanges({
                                                                    ...selectedPromptTool,
                                                                    knowledgeBase: key
                                                                });
                                                            }}
                                                            placeholder={loadingKnowledgeBases ? t('global.loading') : t('ui.selectKnowledgeBase')}
                                                            emptyText={t('ui.noOptions')}
                                                            disabled={loadingKnowledgeBases}
                                                        />
                                                        {!loadingKnowledgeBases && knowledgeBases.length === 0 && (
                                                            <p className="mt-3 text-sm text-muted-foreground">
                                                                {t('ui.noKnowledgeBasesAvailable')}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : null}
                                            </div>

                                            <p className="mt-3 text-sm text-muted-foreground">
                                                {/* Der Agent bekommt keinen Prompt, sondern ein Ziel — der Satz aus dem
                                                    `else`-Zweig beschriebe hier etwas, das es nicht gibt. */}
                                                {selectedPromptTool.promptMode === 'webResearch'
                                                    ? t('ui.promptUsedByAIForWebResearch')
                                                    : selectedPromptTool.promptMode === 'knowledgeBase'
                                                    ? t('ui.promptUsedByAIForKnowledgeBase')
                                                    : selectedPromptTool.promptMode === 'agent'
                                                    ? t('ui.agentStep.goalUsedByAgent')
                                                    : t('ui.promptUsedByAI')
                                                }
                                            </p>
                                        </div>
                                    );
                                case 'inputOutput':
                                    return (
                                        <div className="w-full min-w-0 max-w-[1000px]">
                                            <Tabs
                                                className="mt-6"
                                                value={inputOutputTab}
                                                onValueChange={setInputOutputTab}
                                            >
                                                <TabsList>
                                                    <TabsTrigger value="input">{t('ui.input')}</TabsTrigger>
                                                    <TabsTrigger value="output">{t('ui.output')}</TabsTrigger>
                                                </TabsList>
                                                {/* forceMount: beide Editoren bleiben gemountet, ihre Refs werden
                                                    beim Speichern ausgelesen — auch aus dem inaktiven Reiter. */}
                                                <TabsContent value="input" forceMount hidden={inputOutputTab !== 'input'}>
                                                    <div className="mt-5">
                                                        <ZodEditor
                                                            ref={inputZodEditorRef}
                                                            value={selectedPromptTool.getInputSchemaString()}
                                                            initialFields={selectedPromptTool.getInputSchemaFields()}
                                                            debugName="INPUT"
                                                            onChange={() => setHasUnsavedChanges(true)}
                                                            placeholder={t('ui.dragFieldTypesHereOr')}
                                                            isOutputSchema={false}
                                                        />
                                                    </div>
                                                </TabsContent>
                                                <TabsContent value="output" forceMount hidden={inputOutputTab !== 'output'}>
                                                    <div className="mt-5">
                                                        <ZodEditor
                                                            ref={outputZodEditorRef}
                                                            value={selectedPromptTool.getOutputSchemaString()}
                                                            initialFields={selectedPromptTool.getOutputSchemaFields()}
                                                            debugName="OUTPUT"
                                                            onChange={() => setHasUnsavedChanges(true)}
                                                            placeholder={t('ui.dragFieldTypesHereOr')}
                                                            isOutputSchema={true}
                                                        />
                                                    </div>
                                                </TabsContent>
                                            </Tabs>
                                            <p className="mt-3 text-sm text-muted-foreground">{t('ui.zodSchemasHelpAI')}</p>
                                        </div>
                                    );
                                case 'sampleRequests':
                                    return (
                                        <SampleRequests
                                            type="PromptTool"
                                            marketplaceOffer={selectedPromptTool as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedPromptToolWithChanges}
                                        />
                                    );
                                case 'details':
                                    return (
                                        <MarketingDetails
                                            type="PromptTool"
                                            marketplaceOffer={selectedPromptTool as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedPromptToolWithChanges}
                                            onImagesChange={onImagesChange}
                                        />
                                    );
                                case 'billing':
                                    return (
                                        <PlanPanel
                                            type={PromptTool.type}
                                            marketplaceOffer={selectedPromptTool as MarketplaceOffer}
                                            setMarketplaceOffer={updateSelectedPromptToolWithChanges}
                                        />
                                    );
                                default:
                                    return <div>{t('ui.unknownMenuItem')}</div>;
                            }

    };

    // ---- Create flow: three plain steps for people without technical background ----
    // Describe → Instruction → Finish. Everything technical (input fields, output format, marketplace)
    // sits behind folds in the last step. The steps reuse the fields and sections of the editor
    // (`renderSection`), they only arrange and word them differently.
    const sectionKeys = menuItems.map((item) => item.key);
    const promptMode: string = selectedPromptTool?.promptMode || 'llmPrompt';
    // The steps after the choice of the way of working are its configuration, so they follow the choice:
    // research and knowledge base first set up their source, then comes the instruction; the agent
    // has one step of its own (goal, tools, budget); "only the AI" and planning need just the instruction.
    const modeSteps =
        promptMode === 'webResearch' || promptMode === 'knowledgeBase'
            ? [{ id: 'settings', keys: ['settings'] }, { id: 'instruction', keys: ['prompt'] }]
            : promptMode === 'agent'
              ? [{ id: 'agent', keys: ['agent'] }]
              : [{ id: 'instruction', keys: ['prompt'] }];
    // The keys of steps that have no section in the editor exist only in this flow (see `CREATE_ONLY_KEYS`).
    const createSteps = [
        { id: 'basics', keys: ['general'] },
        { id: 'mode', keys: ['mode'] },
        ...modeSteps,
        { id: 'appearance', keys: ['appearance'] },
        { id: 'finish', keys: ['sampleRequests', 'inputOutput', 'offerStatus', 'details', 'billing'] },
    ];
    const stepIndex = Math.max(0, createSteps.findIndex((step) => step.keys.includes(selectedKey)));
    const currentStep = createSteps[stepIndex];
    const isLastStep = stepIndex === createSteps.length - 1;
    const hasMarketplaceSections = sectionKeys.includes('offerStatus');
    const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
    const [creatingPromptTool, setCreatingPromptTool] = useState(false);

    /** Text-link look that stays readable on any tenant colour (the `link` variant uses the primary colour). */
    const TEXT_LINK = 'h-auto px-0 py-1 text-sm font-normal text-muted-foreground underline underline-offset-4 hover:bg-transparent hover:text-foreground';

    /**
     * The tool with the current state of the schema editors. They keep their fields themselves and
     * unmount with their step, so the state is taken over before leaving the step and before saving.
     */
    const withCurrentSchemas = (tool: any) => {
        const next = new PromptTool(tool);
        Object.assign(next, tool);
        if (inputZodEditorRef.current) {
            next.setInputSchemaFields(inputZodEditorRef.current.getCurrentSchema().fields);
        }
        if (outputZodEditorRef.current) {
            next.setOutputSchemaFields(outputZodEditorRef.current.getCurrentSchema().fields);
        }
        return next;
    };

    // A knowledge base uploaded in another tab shows up when this tab is focused again.
    useEffect(() => {
        if (currentStep.id !== 'settings' || promptMode !== 'knowledgeBase') return;
        const refresh = () => {
            loadKnowledgeBases();
        };
        window.addEventListener('focus', refresh);
        return () => window.removeEventListener('focus', refresh);
    }, [currentStep.id, promptMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const goToStep = (stepId: string) => {
        const target = createSteps.find((step) => step.id === stepId);
        if (!target || target === currentStep) return;
        setSelectedPromptTool(withCurrentSchemas(selectedPromptTool));
        setStepErrors({});
        setSelectedKey(target.keys[0]);
        updateURLFromSelectedKey(target.keys[0]);
    };

    /** Why the step cannot be left yet, or `null`. Shown on the step itself. */
    const stepBlocker = (stepId: string): string | null => {
        if (stepId === 'basics' && !selectedPromptTool?.name?.trim()) {
            return t('ui.promptToolStepper.nameRequired');
        }
        if (stepId === 'settings' && promptMode === 'knowledgeBase' && !selectedPromptTool?.knowledgeBase) {
            return t('ui.promptToolStepper.knowledgeBaseRequired');
        }
        // The agent gets a goal instead of a prompt and is validated by `darfGespeichertWerden`.
        if (stepId === 'instruction' && !selectedPromptTool?.prompt?.trim()) {
            return t('ui.promptToolStepper.instructionRequired');
        }
        return null;
    };

    const nextStep = () => {
        const blocker = stepBlocker(currentStep.id);
        if (blocker) {
            setStepErrors({ [currentStep.id]: blocker });
            return;
        }
        // Agent: the step only passes with a valid agent configuration (the message names why).
        if (currentStep.id === 'agent' && !darfGespeichertWerden(selectedPromptTool)) {
            setStepErrors({ agent: t('ui.promptToolStepper.agentIncomplete') });
            return;
        }
        goToStep(createSteps[stepIndex + 1].id);
    };

    const createFromStepper = async () => {
        const blocked = createSteps.find((step) => stepBlocker(step.id));
        if (blocked) {
            setStepErrors({ [blocked.id]: stepBlocker(blocked.id) as string });
            goToStep(blocked.id);
            return;
        }
        if (!darfGespeichertWerden(selectedPromptTool)) {
            goToStep('agent');
            return;
        }
        setCreatingPromptTool(true);
        try {
            await userAPI.createMarketplaceOffer(authentication.customer, withCurrentSchemas(selectedPromptTool));
            await loadPromptTools();
            updateSelectedPromptTool(null);
        } catch (error) {
            pushError(error);
        } finally {
            setCreatingPromptTool(false);
        }
    };

    /** The "settings" step is the research setup or the knowledge base choice, depending on the way of working. */
    const stepText = (stepId: string, part: 'label' | 'title' | 'description') =>
        t(`ui.promptToolStepper.steps.${stepId === 'settings' ? (promptMode === 'knowledgeBase' ? 'settingsKnowledgeBase' : 'settingsWeb') : stepId}.${part}`);

    /** Step 1: what is the tool called, what is it for. Icon and colour are optional, behind a fold. */
    /** How the tool will look in lists — name, description and icon as they are right now. */
    const renderPreviewCard = () => (
        <SideCard title={t('ui.promptToolStepper.preview.title')}>
            <div className="flex items-start gap-3 rounded-lg border bg-background p-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-muted [&>svg]:size-5">
                    <MarketplaceOfferIcon offer={selectedPromptTool} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className={cn('truncate text-sm font-medium', !selectedPromptTool.name && 'text-muted-foreground')}>
                        {selectedPromptTool.name || t('ui.promptToolStepper.preview.namePlaceholder')}
                    </div>
                    <div className={cn('mt-0.5 line-clamp-3 text-xs', selectedPromptTool.description ? 'text-muted-foreground' : 'text-muted-foreground/60')}>
                        {selectedPromptTool.description || t('ui.promptToolStepper.preview.descriptionPlaceholder')}
                    </div>
                    {selectedPromptTool.published && (
                        <Badge variant="success" className="mt-2">{t('ui.promptToolStepper.preview.internal')}</Badge>
                    )}
                </div>
            </div>
        </SideCard>
    );

    /** Optional step: icon and colour, with the preview next to it. */
    const renderAppearanceStep = () => (
        <StepLayout aside={renderPreviewCard()}>
            <div className="flex flex-wrap items-start gap-8 rounded-xl border bg-card p-5">
                <FluentIconSelector
                    label={t('ui.promptToolStepper.iconLabel')}
                    selectedIcon={selectedPromptTool.icon || 'CityNext'}
                    iconColor={selectedPromptTool.iconColor}
                    onIconChange={(iconName) => {
                        updateSelectedPromptToolWithChanges({ ...selectedPromptTool, icon: iconName });
                    }}
                    placeholder={t('ui.selectIcon')}
                />
                <ColorSelector
                    label={t('ui.promptToolStepper.colorLabel')}
                    selectedColor={selectedPromptTool.iconColor}
                    onColorChange={(color) => {
                        updateSelectedPromptToolWithChanges({ ...selectedPromptTool, iconColor: color });
                    }}
                />
            </div>
            <p className="text-sm text-muted-foreground">{t('ui.promptToolStepper.appearanceHelp')}</p>
        </StepLayout>
    );

    const renderBasicsStep = () => {
        const nameError = stepErrors.basics;
        return (
            <StepLayout
                aside={renderPreviewCard()}
            >
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="prompt-tool-name">{t('ui.promptToolStepper.nameLabel')}</Label>
                    <Input
                        id="prompt-tool-name"
                        value={selectedPromptTool.name}
                        autoFocus
                        placeholder={t('ui.promptToolStepper.namePlaceholder')}
                        aria-required
                        aria-invalid={Boolean(nameError)}
                        aria-describedby={nameError ? 'prompt-tool-name-error' : undefined}
                        className={cn('h-11 text-base', nameError && 'border-destructive focus-visible:ring-destructive')}
                        onChange={(event) => {
                            if (nameError) setStepErrors({});
                            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, name: event.target.value });
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') nextStep();
                        }}
                    />
                    {nameError && (
                        <p id="prompt-tool-name-error" role="alert" className="text-sm text-destructive">
                            {nameError}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="prompt-tool-description">{t('ui.promptToolStepper.descriptionLabel')}</Label>
                    <Textarea
                        id="prompt-tool-description"
                        value={selectedPromptTool.description || ''}
                        rows={5}
                        placeholder={t('ui.promptToolStepper.descriptionPlaceholder')}
                        onChange={(event) => {
                            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, description: event.target.value });
                        }}
                    />
                    <p className="text-sm text-muted-foreground">{t('ui.promptToolStepper.descriptionHelp')}</p>
                </div>
            </StepLayout>
        );
    };

    /** Step: only the choice of the way of working. The steps that follow set it up. */
    const renderModeStep = () => {
        const modeOption = (value: string, icon: ReactNode, recommended = false) => ({
            value,
            title: t(`ui.promptToolStepper.modes.${value}.title`),
            description: t(`ui.promptToolStepper.modes.${value}.description`),
            icon,
            badge: recommended ? t('ui.promptToolStepper.recommended') : undefined,
        });
        return (
            <StepLayout>
                <ChoiceCards
                    name="prompt-tool-mode"
                    hideLabel
                    label={t('ui.promptToolStepper.modeLabel')}
                    value={promptMode}
                    onChange={(mode) => {
                        setStepErrors({});
                        updateSelectedPromptToolWithChanges({
                            ...selectedPromptTool,
                            promptMode: mode,
                            // `agent` is not removed when switching away: whoever switches by mistake and
                            // goes back finds the tool selection and budget again.
                            ...(mode === 'agent'
                                ? { agent: withDefaults(selectedPromptTool.agent), stepSchemaVersion: STEP_SCHEMA_VERSION }
                                : {}),
                        });
                    }}
                    options={[
                        // "Only the AI" is left out: the agent does the same and more.
                        modeOption('agent', <Bot aria-hidden />, true),
                        modeOption('webResearch', <Globe aria-hidden />),
                        modeOption('knowledgeBase', <Library aria-hidden />),
                        modeOption('planning', <ListChecks aria-hidden />),
                    ]}
                />
                {/* What the choice means: what it is good for and what the next steps will ask. */}
                <div aria-live="polite" className="grid gap-5 rounded-xl border bg-muted/30 p-5 sm:grid-cols-2">
                    <div>
                        <div className="mb-1 text-sm font-medium">{t('ui.promptToolStepper.goodFor')}</div>
                        <p className="text-sm text-muted-foreground">{t(`ui.promptToolStepper.modes.${promptMode}.goodFor`)}</p>
                    </div>
                    <div>
                        <div className="mb-1 text-sm font-medium">{t('ui.promptToolStepper.upNext')}</div>
                        <p className="text-sm text-muted-foreground">{t(`ui.promptToolStepper.modes.${promptMode}.next`)}</p>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground">{t('ui.promptToolStepper.changeLater')}</p>
            </StepLayout>
        );
    };

    /** Step: the source of the way of working — research settings or the knowledge base. */
    const renderSettingsStep = () => {
        if (promptMode === 'knowledgeBase') {
            const kbError = stepErrors.settings;
            return (
                <StepLayout>
                    <div className="max-w-xl">
                        <ComboBoxField
                            id="prompt-tool-knowledge-base"
                            label={t('ui.promptToolStepper.knowledgeBaseLabel')}
                            options={knowledgeBases.map((kb) => ({ key: kb._id, text: kb.name }))}
                            selectedKey={selectedPromptTool.knowledgeBase}
                            onSelect={(key) => {
                                setStepErrors({});
                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, knowledgeBase: key });
                            }}
                            placeholder={loadingKnowledgeBases ? t('global.loading') : t('ui.selectKnowledgeBase')}
                            emptyText={t('ui.noOptions')}
                            disabled={loadingKnowledgeBases}
                        />
                        {kbError && (
                            <p role="alert" className="mt-2 text-sm text-destructive">{kbError}</p>
                        )}
                        {!loadingKnowledgeBases && knowledgeBases.length === 0 && (
                            <p className="mt-2 text-sm text-muted-foreground">{t('ui.noKnowledgeBasesAvailable')}</p>
                        )}
                        {/* In a new tab, so the tool being created is not lost; the list refreshes when this tab is focused again. */}
                        <p className="mt-4 text-sm text-muted-foreground">
                            <a
                                href="/settings?panel=agent&key=documents&tab=documentPacks"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-foreground underline underline-offset-4 hover:text-foreground/80"
                            >
                                {t('ui.promptToolStepper.knowledgeBaseUpload')}
                                <ExternalLink className="size-3.5" aria-hidden />
                                <span className="sr-only">{t('ui.promptToolStepper.opensInNewTab')}</span>
                            </a>
                        </p>
                    </div>
                </StepLayout>
            );
        }
        const searchModeOptions: ComboBoxOption[] = [
            { key: 'simple', text: t('ui.promptToolStepper.web.searchModes.simple') },
            { key: 'crawling', text: t('ui.promptToolStepper.web.searchModes.crawling') },
        ];
        const answerTypeOptions: ComboBoxOption[] = [
            { key: 'basic', text: t('ui.promptToolStepper.web.answerTypes.basic') },
            { key: 'advanced', text: t('ui.promptToolStepper.web.answerTypes.advanced') },
        ];
        const webCheckboxes: Array<{ id: string; field: 'includeImages' | 'includeVideos' | 'includeAnswers'; label: string }> = [
            { id: 'prompt-tool-include-images', field: 'includeImages', label: t('ui.includeImages') },
            { id: 'prompt-tool-include-videos', field: 'includeVideos', label: t('ui.includeVideos') },
            { id: 'prompt-tool-include-answers', field: 'includeAnswers', label: t('ui.includeAnswers') },
        ];
        return (
            <StepLayout>
                <div className="grid max-w-3xl gap-6 sm:grid-cols-2">
                    <ComboBoxField
                        id="prompt-tool-search-mode"
                        label={t('ui.promptToolStepper.web.searchMode')}
                        options={searchModeOptions}
                        selectedKey={selectedPromptTool.searchMode || 'simple'}
                        placeholder={t('ui.pleaseSelect')}
                        emptyText={t('ui.noOptions')}
                        onSelect={(key) => {
                            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, searchMode: key });
                        }}
                    />
                    {selectedPromptTool.searchMode === 'crawling' && (
                        <div className="flex min-w-0 flex-col gap-1.5">
                            <Label htmlFor="prompt-tool-search-depth">{t('ui.promptToolStepper.web.searchDepth')}</Label>
                            <Input
                                id="prompt-tool-search-depth"
                                type="number"
                                min={1}
                                value={(selectedPromptTool.searchDepth ?? 3).toString()}
                                onChange={(event) => {
                                    const v = parseInt(event.target.value || '0');
                                    updateSelectedPromptToolWithChanges({ ...selectedPromptTool, searchDepth: isNaN(v) ? undefined : v });
                                }}
                            />
                            <p className="text-sm text-muted-foreground">{t('ui.promptToolStepper.web.searchDepthHelp')}</p>
                        </div>
                    )}
                    <div className="flex min-w-0 flex-col gap-1.5">
                        <Label htmlFor="prompt-tool-max-results">{t('ui.promptToolStepper.web.maxResults')}</Label>
                        <Input
                            id="prompt-tool-max-results"
                            type="number"
                            min={1}
                            value={selectedPromptTool.maxResults?.toString() || '10'}
                            onChange={(event) => {
                                updateSelectedPromptToolWithChanges({ ...selectedPromptTool, maxResults: parseInt(event.target.value || '10') });
                            }}
                        />
                    </div>
                    <ComboBoxField
                        id="prompt-tool-include-answer"
                        label={t('ui.promptToolStepper.web.answerType')}
                        options={answerTypeOptions}
                        selectedKey={selectedPromptTool.includeAnswer || 'basic'}
                        placeholder={t('ui.pleaseSelect')}
                        emptyText={t('ui.noOptions')}
                        onSelect={(key) => {
                            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, includeAnswer: key });
                        }}
                    />
                    <div className="flex flex-col gap-3 sm:col-span-2">
                        <div className="text-sm font-medium">{t('ui.promptToolStepper.web.alsoInclude')}</div>
                        {webCheckboxes.map((box) => (
                            <div key={box.id} className="flex items-center gap-2">
                                <Checkbox
                                    id={box.id}
                                    checked={selectedPromptTool[box.field] || false}
                                    onCheckedChange={(checked) => {
                                        updateSelectedPromptToolWithChanges({ ...selectedPromptTool, [box.field]: checked === true });
                                    }}
                                />
                                <Label htmlFor={box.id} className="cursor-pointer font-normal">{box.label}</Label>
                            </div>
                        ))}
                    </div>
                </div>
            </StepLayout>
        );
    };

    /** Step: the agent — goal, tools, budget (its own panel). */
    const renderAgentStep = () => (
        <StepLayout>
            <AgentStepPanel
                config={withDefaults(selectedPromptTool.agent)}
                tools={toolCatalogue}
                catalogueError={toolCatalogueError}
                showValidation={Boolean(stepErrors.agent)}
                onChange={(agent) => {
                    if (stepErrors.agent) setStepErrors({});
                    updateSelectedPromptToolWithChanges({
                        ...selectedPromptTool,
                        agent,
                        stepSchemaVersion: STEP_SCHEMA_VERSION,
                    });
                }}
            />
            <p className="text-sm text-muted-foreground">{t('ui.agentStep.goalUsedByAgent')}</p>
        </StepLayout>
    );

    /** Step: the instruction in the user's own words, with examples, tips and the field markers. */
    const renderInstructionStep = () => {
        const instructionError = stepErrors.instruction;
        const inputFields = selectedPromptTool.getInputSchemaFields ? selectedPromptTool.getInputSchemaFields() : [];
        const outputFields = selectedPromptTool.getOutputSchemaFields ? selectedPromptTool.getOutputSchemaFields() : [];
        const modeHelp: Record<string, string> = {
            webResearch: t('ui.promptUsedByAIForWebResearch'),
            knowledgeBase: t('ui.promptUsedByAIForKnowledgeBase'),
        };
        const examplePrompts = ['summarize', 'email', 'answer'];
        return (
            <StepLayout
                aside={
                    <SideCard title={t('ui.promptToolStepper.tips.title')}>
                        <ul className="flex list-disc flex-col gap-2 pl-4 text-sm text-muted-foreground marker:text-muted-foreground/60">
                            <li>{t('ui.promptToolStepper.tips.one')}</li>
                            <li>{t('ui.promptToolStepper.tips.two')}</li>
                            <li>{t('ui.promptToolStepper.tips.three')}</li>
                        </ul>
                    </SideCard>
                }
            >
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="prompt-tool-instruction">{t('ui.promptToolStepper.promptLabel')}</Label>
                    {!(selectedPromptTool.prompt || '').trim() && (
                        <div className="flex flex-wrap items-center gap-2" aria-label={t('ui.promptToolStepper.examples.title')}>
                            <span className="text-sm text-muted-foreground">{t('ui.promptToolStepper.examples.title')}</span>
                            {examplePrompts.map((key) => (
                                <Button
                                    key={key}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 rounded-full px-3 text-xs"
                                    onClick={() => {
                                        setStepErrors({});
                                        updateSelectedPromptToolWithChanges({
                                            ...selectedPromptTool,
                                            prompt: t(`ui.promptToolStepper.examples.${key}.text`),
                                        });
                                    }}
                                >
                                    {t(`ui.promptToolStepper.examples.${key}.label`)}
                                </Button>
                            ))}
                        </div>
                    )}
                    <Textarea
                        id="prompt-tool-instruction"
                        value={selectedPromptTool.prompt || ''}
                        rows={10}
                        placeholder={t('ui.promptToolStepper.promptPlaceholder')}
                        aria-required
                        aria-invalid={Boolean(instructionError)}
                        aria-describedby={instructionError ? 'prompt-tool-instruction-error' : undefined}
                        className={cn(instructionError && 'border-destructive focus-visible:ring-destructive')}
                        onChange={(event) => {
                            if (instructionError) setStepErrors({});
                            updateSelectedPromptToolWithChanges({ ...selectedPromptTool, prompt: event.target.value });
                        }}
                    />
                    {instructionError && (
                        <p id="prompt-tool-instruction-error" role="alert" className="text-sm text-destructive">
                            {instructionError}
                        </p>
                    )}
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
                            {modeHelp[promptMode] ?? t('ui.promptToolStepper.promptHelp')}
                        </p>
                        <span className="text-xs text-muted-foreground">
                            {formatTokenEstimation(estimateTokens(selectedPromptTool.prompt || ''))}
                        </span>
                    </div>
                </div>

                {/* Fields defined in the technical part of the last step; a click on one opens it there. */}
                {(inputFields.length > 0 || outputFields.length > 0) && (
                    <div className="flex flex-col gap-4 rounded-xl border bg-muted/30 p-4">
                        {inputFields.length > 0 && (
                            <div>
                                <div className="mb-2 text-sm font-medium">{t('ui.promptToolStepper.inputFieldsTitle')}</div>
                                {renderParameterChips(inputFields, t('ui.userInput'), t('ui.userInputDescription'), 'input')}
                            </div>
                        )}
                        {outputFields.length > 0 && (
                            <div>
                                <div className="mb-2 text-sm font-medium">{t('ui.promptToolStepper.outputFieldsTitle')}</div>
                                {renderParameterChips(outputFields, t('ui.promptOutput'), t('ui.promptOutputDescription'), 'output')}
                            </div>
                        )}
                    </div>
                )}
            </StepLayout>
        );
    };

    /** Step 3: check, decide who may use it, optionally add examples; the technical rest is folded away. */
    const renderFinishStep = () => {
        const modeTitle = t(`ui.promptToolStepper.modes.${promptMode}.title`);
        const knowledgeBaseName = knowledgeBases.find((kb) => kb._id === selectedPromptTool.knowledgeBase)?.name;
        const summary: Array<{ label: string; value: string; step: string }> = [
            { label: t('ui.promptToolStepper.nameLabel'), value: selectedPromptTool.name, step: 'basics' },
            { label: t('ui.promptToolStepper.summaryPurpose'), value: selectedPromptTool.description, step: 'basics' },
            { label: t('ui.promptToolStepper.summaryHow'), value: modeTitle, step: 'mode' },
            ...(promptMode === 'knowledgeBase'
                ? [{ label: t('ui.promptToolStepper.knowledgeBaseSummary'), value: knowledgeBaseName ?? '', step: 'settings' }]
                : []),
            ...(promptMode === 'agent'
                ? [{ label: t('ui.promptToolStepper.goalSummary'), value: truncate(selectedPromptTool.agent?.goal || '', { length: 160 }), step: 'agent' }]
                : [{ label: t('ui.promptToolStepper.promptLabel'), value: truncate(selectedPromptTool.prompt || '', { length: 160 }), step: 'instruction' }]),
        ];
        return (
            <StepLayout
                aside={
                    <SideCard title={t('ui.promptToolStepper.whoTitle')}>
                        <InternallyAvailableCheckbox
                            marketplaceOffer={selectedPromptTool}
                            updateSelectedMarketplaceOffer={updateSelectedPromptToolWithChanges}
                        />
                        <p className="text-sm text-muted-foreground">{t('ui.promptToolStepper.whoHelp')}</p>
                    </SideCard>
                }
            >
                <dl className="divide-y rounded-xl border bg-card">
                    {summary.map((row) => (
                        <div key={row.label} className="flex items-start gap-4 p-4">
                            <dt className="w-32 shrink-0 text-sm text-muted-foreground">{row.label}</dt>
                            <dd className="min-w-0 flex-1 text-sm">{row.value || '—'}</dd>
                            <Button variant="ghost" className={TEXT_LINK} onClick={() => goToStep(row.step)}>
                                {t('ui.promptToolStepper.change')}
                            </Button>
                        </div>
                    ))}
                </dl>

                <Disclosure
                    title={t('ui.promptToolStepper.examplesTitle')}
                    description={t('ui.promptToolStepper.examplesHelp')}
                >
                    {renderSection('sampleRequests')}
                </Disclosure>

                {/* Input fields and answer format: on the page itself, not behind a fold. */}
                <section aria-labelledby="prompt-tool-section-io" className="min-w-0 rounded-xl border bg-card p-5">
                    <h4 id="prompt-tool-section-io" className="text-sm font-medium">
                        {t('ui.promptToolStepper.ioTitle')}
                    </h4>
                    <p className="mb-4 mt-0.5 text-sm text-muted-foreground">{t('ui.promptToolStepper.ioHelp')}</p>
                    {renderSection('inputOutput')}
                </section>

                {hasMarketplaceSections && (
                    <Disclosure
                        title={t('ui.promptToolStepper.marketplaceTitle')}
                        description={t('ui.promptToolStepper.marketplaceHelp')}
                    >
                        <div className="flex min-w-0 flex-col gap-10">
                            {['offerStatus', 'details', 'billing']
                                .filter((key) => sectionKeys.includes(key))
                                .map((key) => (
                                    <section key={key} aria-labelledby={`prompt-tool-section-${key}`} className="min-w-0">
                                        <h4 id={`prompt-tool-section-${key}`} className="mb-4 border-b pb-2 text-sm font-semibold">
                                            {String(menuItems.find((item) => item.key === key)?.name ?? key)}
                                        </h4>
                                        {renderSection(key)}
                                    </section>
                                ))}
                        </div>
                    </Disclosure>
                )}
            </StepLayout>
        );
    };

    const renderCreateStep = (stepId: string): ReactNode => {
        switch (stepId) {
            case 'basics': return renderBasicsStep();
            case 'mode': return renderModeStep();
            case 'settings': return renderSettingsStep();
            case 'agent': return renderAgentStep();
            case 'instruction': return renderInstructionStep();
            case 'appearance': return renderAppearanceStep();
            default: return renderFinishStep();
        }
    };

    const createFlow = selectedPromptTool && !selectedPromptTool._id ? (
        <div className="w-full min-w-0">
            {/* One compact header row: title, steps, cancel — all three share the line of the step markers.
                Below the large breakpoint the steps drop under the title. */}
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-10">
                <div className="flex items-start justify-between gap-3 lg:contents">
                    <div className="flex h-7 min-w-0 items-center gap-2 lg:w-56 lg:shrink-0">
                        <span className="shrink-0 text-sm text-muted-foreground">Prompt-Werkzeug</span>
                        <span aria-hidden className="text-muted-foreground/50">/</span>
                        <h2 className="truncate text-sm font-semibold">{selectedPromptTool.name || t('ui.addPromptTool')}</h2>
                    </div>
                    <div className="flex h-7 shrink-0 items-center lg:order-last lg:w-56 lg:justify-end">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 text-muted-foreground hover:text-foreground"
                            onClick={() => updateSelectedPromptTool(null)}
                            disabled={creatingPromptTool}
                        >
                            <X className="size-4" aria-hidden />
                            {t('ui.cancel')}
                        </Button>
                    </div>
                </div>
                <Stepper
                    className="min-w-0 lg:mx-auto lg:max-w-2xl lg:flex-1"
                    label={t('ui.promptToolStepper.label')}
                    steps={createSteps.map((step) => ({
                        id: step.id,
                        label: stepText(step.id, 'label'),
                        error: stepErrors[step.id],
                    }))}
                    current={stepIndex}
                    onStepSelect={(index) => goToStep(createSteps[index].id)}
                />
            </div>

            <div className="mt-10 min-w-0 border-t pt-8">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={currentStep.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4, transition: { duration: 0.12, ease: EASE_OUT } }}
                        transition={{ duration: 0.24, ease: EASE_OUT }}
                    >
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold">{stepText(currentStep.id, 'title')}</h3>
                            <p className="mt-1 max-w-prose text-sm text-muted-foreground">
                                {stepText(currentStep.id, 'description')}
                            </p>
                        </div>
                        {renderCreateStep(currentStep.id)}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="sticky bottom-0 z-10 mt-8 flex items-center justify-between gap-3 border-t bg-background/95 py-4 backdrop-blur">
                <Button
                    variant="outline"
                    disabled={stepIndex === 0 || creatingPromptTool}
                    onClick={() => goToStep(createSteps[stepIndex - 1].id)}
                >
                    {t('ui.promptToolStepper.back')}
                </Button>
                <div className="flex items-center gap-3">
                    <span className="hidden text-sm text-muted-foreground sm:inline" aria-hidden>
                        {t('ui.promptToolStepper.progress', { current: stepIndex + 1, total: createSteps.length })}
                    </span>
                    {isLastStep ? (
                        <Button onClick={createFromStepper} disabled={creatingPromptTool}>
                            {creatingPromptTool && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            {t('ui.promptToolStepper.create')}
                        </Button>
                    ) : (
                        <Button onClick={nextStep}>{t('ui.promptToolStepper.next')}</Button>
                    )}
                </div>
            </div>
        </div>
    ) : null;

    return <><div className="w-full min-w-0">
        {selectedPromptTool && !selectedPromptTool._id
            ?
            createFlow
            : selectedPromptTool
            ?
            <div className="w-full min-w-0 rounded-md border p-3">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="min-w-0">
                        <div className="text-sm text-muted-foreground">Prompt-Werkzeug</div>
                        <h2 className="text-base font-semibold">{selectedPromptTool.name}</h2>
                    </div>
                    <div className="flex flex-1 flex-wrap items-center justify-end gap-4">
                        {hasUnsavedChanges && (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-warning/40 bg-warning/10 px-2.5 py-0.5 text-sm text-warning">
                                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                                {t('global.unsavedChanges')}
                            </span>
                        )}
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={t('ui.test')}
                                        onClick={() => openTestDialog(selectedPromptTool)}
                                    >
                                        <Rocket className="h-4 w-4" aria-hidden />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('ui.test')}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <Button variant="outline" onClick={() => {
                            updateSelectedPromptTool(null);
                        }}>{hasUnsavedChanges ? t('ui.cancel') : t('global.close')}</Button>
                        {selectedPromptTool._id && selectedPromptTool.lastStatus !== OfferStatus.discontinued ?
                            <Button onClick={async () => {
                                if (!darfGespeichertWerden(selectedPromptTool)) return;

                                // Get current schema from ZodEditor refs
                                let updatedPromptTool = new PromptTool(selectedPromptTool);
                                Object.assign(updatedPromptTool, selectedPromptTool);

                                if (inputZodEditorRef.current) {
                                    const inputSchema = inputZodEditorRef.current.getCurrentSchema();
                                    updatedPromptTool.setInputSchemaFields(inputSchema.fields);
                                }

                                if (outputZodEditorRef.current) {
                                    const outputSchema = outputZodEditorRef.current.getCurrentSchema();
                                    updatedPromptTool.setOutputSchemaFields(outputSchema.fields);
                                }

                                await userAPI.updateMarketplaceOffer(authentication.customer, updatedPromptTool);
                                await loadPromptTools();
                                updateSelectedPromptTool(null);
                            }}>{t('ui.save')}</Button>
                            :
                            <Button onClick={async () => {
                                if (!darfGespeichertWerden(selectedPromptTool)) return;

                                // Get current schema from ZodEditor refs
                                let updatedPromptTool = new PromptTool(selectedPromptTool);
                                Object.assign(updatedPromptTool, selectedPromptTool);

                                if (inputZodEditorRef.current) {
                                    const inputSchema = inputZodEditorRef.current.getCurrentSchema();
                                    updatedPromptTool.setInputSchemaFields(inputSchema.fields);
                                }

                                if (outputZodEditorRef.current) {
                                    const outputSchema = outputZodEditorRef.current.getCurrentSchema();
                                    updatedPromptTool.setOutputSchemaFields(outputSchema.fields);
                                }

                                await userAPI.createMarketplaceOffer(authentication.customer, updatedPromptTool);
                                await loadPromptTools();
                                updateSelectedPromptTool(null);
                            }}>{t('ui.create')}</Button>
                        }
                    </div>
                </div>
                <div className="mt-3 flex min-w-0 flex-col gap-5 rounded-md border p-3 md:flex-row">
                    <div className="w-full shrink-0 md:w-[200px]">
                        {menuItems.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={cn(
                                    'flex w-full items-center p-3 text-left text-sm hover:bg-accent',
                                    selectedKey === item.key && 'bg-accent',
                                )}
                                onClick={item.onClick}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                    <div className="w-full min-w-0 flex-1">
                        {renderSection(selectedKey)}
                    </div>
                </div>
            </div>
            :
            <div className="mt-5">
                <div className="flex flex-wrap items-center gap-3">
                    <NameFilterBar
                        wert={promptToolFilter}
                        onChange={setPromptToolFilter}
                        seite={page}
                        onSeiteChange={onPageChange}
                        anzahl={gefiltertePromptTools.length}
                        gesamt={(promptTools ?? []).length}
                    />
                    <Button variant="ghost" onClick={createNewPromptTool}>
                        <Plus className="mr-2 h-4 w-4" aria-hidden />
                        {t('ui.addPromptTool')}
                    </Button>
                </div>

                <div className="mt-5 overflow-x-auto">
                    <DataTable
                        columns={promptToolColumns}
                        data={gefiltertePromptTools}
                        page={page}
                        onPageChange={onPageChange}
                        loading={loadingPromptTools && promptTools.length === 0}
                        onRowClick={updateSelectedPromptTool}
                        emptyMessage="Keine Prompt-Werkzeuge gefunden"
                    />
                </div>
            </div>
        }

        <Dialog open={linkDialogOpen} onOpenChange={(open) => { if (!open) { setLinkDialogOpen(false); } }}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('ui.addLink')}</DialogTitle>
                </DialogHeader>
                <div className="mt-5 flex flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="prompt-tool-link-url">
                            URL
                            <span className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id="prompt-tool-link-url"
                            value={linkUrl}
                            onChange={(event) => setLinkUrl(event.target.value)}
                            required
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="prompt-tool-link-description">Beschreibung</Label>
                        <Textarea
                            id="prompt-tool-link-description"
                            rows={3}
                            onChange={(event) => setLinkUrl(event.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>Abbrechen</Button>
                    <Button type="button" onClick={() => {
                        const newLink = {
                            _id: Date.now().toString(),
                            url: linkUrl,
                            description: linkUrl
                        };
                                                        updateSelectedPromptToolWithChanges({
                            ...selectedPromptTool,
                            links: [...(selectedPromptTool.links || []), newLink]
                        });
                        setLinkUrl('');
                        setLinkDialogOpen(false);
                    }}>{t('ui.add')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {TestDialog}
    </div></>;
}
